import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import type {Flow} from "flow-to-typescript-codemod";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@repo/analytics-learning-time";
import {GuideStateApi} from "@repo/guide-state";

interface Props {
    /* When true, the button is shown; otherwise, it is not. */
    show: boolean;
    /* A function the button will invoke when clicked. */
    clickHandler: () => unknown;
}

export const StopResponseButton: Flow.AbstractComponent<
    Props,
    React.ReactNode
> = React.memo(({show, clickHandler}: Props): React.ReactElement | null => {
    const onClick = React.useCallback(() => {
        clickHandler();
        const stopId = GuideStateApi.latest().stopId;
        if (stopId) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideStopResponseClicked",
                eventSchemaVersion: 2,
                aiGuideThreadId: GuideStateApi.latest().threadId ?? "",
                aiGuideInteractionId: stopId,
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
            });
        }
    }, [clickHandler]);

    if (!show) {
        return null;
    }

    return (
        <View style={styles.stopResponseWrapper}>
            <Clickable
                onClick={onClick}
                testId="stop-response-button"
                style={styles.noOutline}
            >
                {() => (
                    <View style={styles.stopResponse}>
                        <LabelXSmall
                            style={[styles.labelText, styles.noOutline]}
                        >
                            {t`Stop response`}
                        </LabelXSmall>
                    </View>
                )}
            </Clickable>
        </View>
    );
});

const styles = StyleSheet.create({
    stopResponseWrapper: {
        borderRadius: 6,
        display: "flex",
        justifyContent: "center",
        width: "fit-content",
    },
    stopResponse: {
        justifyContent: "center",
        width: "fit-content",
        background: color.offWhite,
        borderRadius: 6,
        border: `1px solid ${color.offBlack16}`,
        padding: `0px ${spacing.xSmall_8}px`,
        margin: 0,
        height: 24,
        ":hover": {
            border: `1px solid ${color.blue}`,
        },
        ":active": {
            backgroundColor: color.fadedBlue8,
        },
        ":focus": {
            border: `inset 1px solid ${color.blue}`,
        },
    },
    labelText: {
        color: color.blue,
        textAlign: "start",
    },
    noOutline: {
        ":focus": {
            outline: "none",
        },
    },
});
